import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../../api";
import { GUIDELINES_ENABLED } from "../../config";
import { Icon, Empty, Spinner, useToast } from "../../components/ui";
import { derive, applyRefData } from "../admin/recordDerive";
import { RecordModal } from "../admin/Inventory";

const pad = (n) => String(n).padStart(2, "0");

// Which day a record belongs to in MY work: the date THIS user last worked it — not the record's global
// workedAt (which another process's worker can bump to a later day, making a record I worked last week drift
// into "today"). listMyWorked overlays the user's own last round, so dateRequested (MM/DD/YYYY) is that date;
// fall back to workedAt when there's no such round (e.g. a record kept in Reviewed via Save & close).
function myWorkDay(r) {
  const mdy = r && r.dateRequested;
  if (mdy && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(mdy)) {
    const [mm, dd, yyyy] = mdy.split("/").map(Number);
    const d = new Date(yyyy, mm - 1, dd);
    if (!isNaN(d)) return { key: `${yyyy}-${pad(mm)}-${pad(dd)}`, mdy: `${pad(mm)}/${pad(dd)}/${yyyy}`, sortv: d.getTime() };
  }
  try {
    const d = new Date(r.workedAt);
    return { key: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, mdy: `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`, sortv: d.getTime() };
  } catch { return { key: "unknown", mdy: "Unknown date", sortv: 0 }; }
}
// status → accent colour (same palette as the rest of the app)
const SCOL = {
  Approved: "#15803d", "PA Not Required": "#15803d", Denied: "#b91c1c",
  Canceled: "#b45309", Withdraw: "#b45309", Pending: "#1d4ed8",
  Reviewed: "#7c3aed", Faxed: "#7c3aed", "Submitted via call": "#7c3aed",
};
// group records by the day THIS user worked them (see myWorkDay), newest day first; label in MM/DD/YYYY
function groupByDay(rows) {
  const map = new Map();
  rows.forEach((r) => {
    const { key, mdy, sortv } = myWorkDay(r);
    if (!map.has(key)) map.set(key, { key, mdy, sortv, items: [] });
    map.get(key).items.push(r);
  });
  return Array.from(map.values()).sort((a, b) => b.sortv - a.sortv);
}

export function MyWork() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [q, setQ] = useState("");
  const [dateFilter, setDateFilter] = useState("");   // YYYY-MM-DD; empty = all days
  const [glCodes, setGlCodes] = useState({});          // recordId -> guideline id used

  const load = useCallback(async () => {
    try {
      const [recs, ref, codes] = await Promise.all([api.myWorked(), api.getRefData().catch(() => null), (GUIDELINES_ENABLED ? api.myGuidelineCodes().catch(() => ({})) : Promise.resolve({}))]);
      applyRefData(ref); setRows(recs); setGlCodes(codes || {});
    } catch (e) { toast(e.message, "err"); setRows([]); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const derived = useMemo(() => { const m = new Map(); (rows || []).forEach((r) => m.set(r.id, derive(r))); return m; }, [rows]);
  const dayKey = (r) => myWorkDay(r).key;
  const filtered = useMemo(() => (rows || []).filter((r) =>
    (!q.trim() || String(r.mrn || "").includes(q.trim())) &&
    (!dateFilter || dayKey(r) === dateFilter)
  ), [rows, q, dateFilter]);
  const days = useMemo(() => groupByDay(filtered), [filtered]);

  if (rows === null) return <div className="empty"><Spinner /></div>;

  return (
    <div className="mw-view">
      <div className="ug-toolbar">
        <div className="mw-title">My work<span className="mw-title-n">{rows.length}</span></div>
        <div className="search ug-search"><Icon.search /><input className="inp" placeholder="Search PID (MRN)…" value={q} onChange={(e) => setQ(e.target.value)} />{q && <button className="ds-clear" onClick={() => setQ("")}><Icon.x width="13" height="13" /></button>}</div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <input type="date" className="inp" style={{ width: 158, height: 40 }} value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} title="Show one day" />
          {dateFilter && <button className="ds-clear" onClick={() => setDateFilter("")} title="Show all days"><Icon.x width="13" height="13" /></button>}
        </div>
      </div>

      {days.length === 0
        ? <Empty icon={<Icon.clipboard width="40" height="40" />} title="Nothing yet" text="Records you work will appear here, separated by day." />
        : <div className="card uv-grid-card" style={{ overflow: "hidden" }}><div className="table-wrap grid-scroll" style={{ overflowY: "auto", overflowX: "hidden" }}>
            <table className="tbl select-tbl user-grid">
              <colgroup>
                <col style={{ width: "10%" }} />
                <col style={{ width: "21%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "19%" }} />
                <col style={{ width: "9%" }} />
                {GUIDELINES_ENABLED && <col style={{ width: "9%" }} />}
              </colgroup>
              <thead><tr>
                <th>PID (MRN)</th><th>Unique ID</th><th>Group</th><th>Clinic Location</th><th>Order Date</th><th>Status</th><th>Reason</th><th>Inbox</th>{GUIDELINES_ENABLED && <th>Guideline ID</th>}
              </tr></thead>
              <tbody>
                {days.map((day) => (
                  <React.Fragment key={day.key}>
                    <tr className="mw-sep"><td colSpan={GUIDELINES_ENABLED ? 9 : 8}>
                      <span className="mw-sep-date">{day.mdy}</span>
                      <span className="mw-sep-n">{day.items.length} {day.items.length === 1 ? "record" : "records"}</span>
                    </td></tr>
                    {day.items.map((r) => {
                      const d = derived.get(r.id) || {};
                      return (
                        <tr key={r.id} className="row-click" onClick={() => setViewing(r)}>
                          <td className="mono">{r.mrn}</td>
                          <td className="mono cell-sub uid-cell" title={r.uniqueId}>{r.uniqueId}</td>
                          <td>{r.productionGroup || d.location || "—"}</td>
                          <td className="cell-sub wrap">{d.clinic || "—"}</td>
                          <td className="cell-sub">{d.orderDate || "—"}</td>
                          <td>{r.paStatus ? <span style={{ fontWeight: 700, color: SCOL[r.paStatus] || "#334155" }}>{r.paStatus}</span> : <span className="cell-sub">—</span>}</td>
                          <td className="wrap" title={r.reason || r.uploaderNote || ""}>{r.reason || r.uploaderNote || "—"}</td>
                          <td className="cell-sub wrap">{r.inboxValue || "—"}</td>
                          {GUIDELINES_ENABLED && <td className="mono">{glCodes[r.id] === "NONE" ? <span className="cell-sub">No guideline</span> : (glCodes[r.id] || <span className="cell-sub">—</span>)}</td>}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div></div>}

      {viewing && <RecordModal record={viewing} onClose={() => setViewing(null)} mine />}
    </div>
  );
}